// src/components/analytics/AnalyticsAIBriefing.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — AI briefing at the top of the Analytics Hub.
//
// Props
//   dateRange   { startDate: Date, endDate: Date }   the page's range
//   focus       tab slug (e.g. 'sla', 'predictive-health') — the briefing ranks those findings first
//   liveVersion number from useAnalyticsLive — a change means "something relevant happened";
//               the briefing refreshes (at most every 20 s)
//   onAsk(question)  open Ask FlowSense AI with a question about the briefing
//   onNavigate(path) open a screen named in a finding
//
// Data: GET /ml-ops/insights/briefing (KPIs vs the previous period, live risk counts, model
// status, findings with evidence, and a narrative written by Ollama or the built-in composer).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Flex, HStack, VStack, Text, Heading, Badge, Button, IconButton, Tooltip, Icon,
  SimpleGrid, Skeleton, SkeletonText, Alert, AlertIcon, AlertDescription, Collapse,
  CircularProgress, CircularProgressLabel, Divider, Wrap, WrapItem, useColorModeValue,
} from '@chakra-ui/react';
import {
  MdAutoAwesome, MdRefresh, MdExpandMore, MdExpandLess, MdArrowUpward, MdArrowDownward,
  MdRemove, MdErrorOutline, MdWarningAmber, MdInfoOutline, MdCheckCircleOutline, MdChevronRight,
  MdQuestionAnswer,
} from 'react-icons/md';
import { getAiBriefing } from '../../services/analyticsService';
import { MiniMarkdown } from '../ml/FlowSenseAIExplainer';
import { timeAgo } from '../ml/mlFormat';

const COLLAPSE_KEY = 'flowsense.analytics.briefing.collapsed';
const MIN_LIVE_GAP_MS = 20000;
const SEVERITY = {
  critical: { color: 'red', icon: MdErrorOutline, label: 'Critical' },
  warning: { color: 'orange', icon: MdWarningAmber, label: 'Attention' },
  info: { color: 'blue', icon: MdInfoOutline, label: 'Info' },
  success: { color: 'green', icon: MdCheckCircleOutline, label: 'Good' },
};
const SCORE_COLOR = (s) => (s >= 85 ? 'green' : s >= 65 ? 'yellow' : s >= 40 ? 'orange' : 'red');

const readCollapsed = () => { try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch (_) { return false; } };
const writeCollapsed = (v) => { try { localStorage.setItem(COLLAPSE_KEY, v ? '1' : '0'); } catch (_) { /* storage blocked */ } };

const fmtKpi = (k, v) => {
  if (v === null || v === undefined) return '—';
  if (k.unit === '%') return `${Number(v).toFixed(1)}%`;
  if (k.unit === '$') return `$${Math.round(v).toLocaleString()}`;
  return Math.round(v).toLocaleString();
};
const fmtChange = (k) => {
  const c = k.change;
  if (!c || c.delta === null || c.delta === undefined) return 'no earlier data';
  if (k.unit === '%') return `${c.delta >= 0 ? '+' : ''}${Number(c.delta).toFixed(1)} pts`;
  if (c.pct === null || c.pct === undefined) return `${c.delta >= 0 ? '+' : ''}${Math.round(c.delta)}`;
  return `${c.pct >= 0 ? '+' : ''}${Number(c.pct).toFixed(1)}%`;
};

/** Tiny dependency-free sparkline (the page already loads recharts for heavier charts). */
function Sparkline({ points, color, width = 120, height = 28 }) {
  const path = useMemo(() => {
    const vals = (points || []).map(p => (p.value === null || p.value === undefined ? null : Number(p.value)));
    const nums = vals.filter(v => v !== null);
    if (nums.length < 2) return null;
    const min = Math.min(...nums); const max = Math.max(...nums);
    const span = max - min || 1;
    const step = width / Math.max(vals.length - 1, 1);
    let d = ''; let pen = false;
    vals.forEach((v, i) => {
      if (v === null) { pen = false; return; }
      const x = (i * step).toFixed(1);
      const y = (height - 2 - ((v - min) / span) * (height - 4)).toFixed(1);
      d += `${pen ? 'L' : 'M'}${x},${y} `;
      pen = true;
    });
    return d.trim();
  }, [points, width, height]);
  if (!path) return <Box h={`${height}px`} />;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" style={{ display: 'block' }}>
      <path d={path} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function KpiTile({ k, tileBg, border, muted, sparkColors }) {
  const a = k.change?.assessment;
  const delta = k.change?.delta;
  const arrow = delta === null || delta === undefined || delta === 0 ? MdRemove : (delta > 0 ? MdArrowUpward : MdArrowDownward);
  const tone = a === 'better' ? 'green' : a === 'worse' ? 'red' : 'gray';
  return (
    <Box bg={tileBg} borderWidth="1px" borderColor={border} borderRadius="lg" p={3} minW={0}>
      <Text fontSize="xs" color={muted} noOfLines={1}>{k.label}</Text>
      <HStack justify="space-between" align="flex-end" spacing={2} mt={1}>
        <VStack align="flex-start" spacing={0} minW={0}>
          <Text fontSize="xl" fontWeight="bold" lineHeight="short">{k.available ? fmtKpi(k, k.current) : '—'}</Text>
          <HStack spacing={1}>
            <Icon as={arrow} color={`${tone}.400`} boxSize={3.5} />
            <Text fontSize="xs" color={`${tone}.400`} fontWeight="medium">{k.available ? fmtChange(k) : 'not available'}</Text>
          </HStack>
        </VStack>
        <Box flexShrink={0}><Sparkline points={k.series} color={sparkColors[tone]} width={96} /></Box>
      </HStack>
      {k.trend?.direction && k.trend.direction !== 'flat' && (
        <Text fontSize="2xs" color={muted} mt={1}>Trend: {k.trend.direction} over the range</Text>
      )}
    </Box>
  );
}

export default function AnalyticsAIBriefing({ dateRange, focus = null, liveVersion = 0, onAsk, onNavigate }) {
  // ── theme (hoisted) ──
  const cardBg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('purple.100', 'whiteAlpha.200');
  const tileBg = useColorModeValue('gray.50', 'gray.700');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const accentBg = useColorModeValue('purple.50', 'whiteAlpha.100');
  const insightHover = useColorModeValue('gray.50', 'whiteAlpha.50');
  const spark = {
    green: useColorModeValue('#38A169', '#68D391'),
    red: useColorModeValue('#E53E3E', '#FC8181'),
    gray: useColorModeValue('#718096', '#A0AEC0'),
  };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [showAllInsights, setShowAllInsights] = useState(false);

  const mounted = useRef(true);
  const abortRef = useRef(null);
  const lastFetchAt = useRef(0);
  const liveTimer = useRef(null);

  const startIso = dateRange?.startDate ? new Date(dateRange.startDate).toISOString().slice(0, 10) : null;
  const endIso = dateRange?.endDate ? new Date(dateRange.endDate).toISOString().slice(0, 10) : null;

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    abortRef.current = ctrl;
    if (quiet) setRefreshing(true); else setLoading(true);
    lastFetchAt.current = Date.now();
    try {
      const d = await getAiBriefing({ startDate: startIso, endDate: endIso, focus, signal: ctrl?.signal });
      if (!mounted.current || ctrl?.signal?.aborted) return;
      setData(d); setError(null);
    } catch (e) {
      if (!mounted.current || e?.name === 'AbortError' || e?.name === 'CanceledError' || ctrl?.signal?.aborted) return;
      setError(e);
    } finally {
      if (mounted.current && abortRef.current === ctrl) { setLoading(false); setRefreshing(false); }
    }
  }, [startIso, endIso, focus]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (abortRef.current) abortRef.current.abort();
      if (liveTimer.current) clearTimeout(liveTimer.current);
    };
  }, []);

  // range / focus changes → load now
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; load(); }, [load]);

  // live events → quiet refresh, at most every MIN_LIVE_GAP_MS
  useEffect(() => {
    if (!liveVersion) return undefined;
    if (liveTimer.current) return undefined; // one pending refresh is enough
    const wait = Math.max(2000, MIN_LIVE_GAP_MS - (Date.now() - lastFetchAt.current));
    liveTimer.current = setTimeout(() => { liveTimer.current = null; loadRef.current({ quiet: true }); }, wait);
    return undefined;
  }, [liveVersion]);

  const toggle = useCallback(() => setCollapsed((c) => { writeCollapsed(!c); return !c; }), []);
  const ask = useCallback(() => {
    const q = data
      ? `Explain the current analytics briefing for ${data.range.start} to ${data.range.end}: why the health score is ${data.score}/100 and what I should do first.`
      : 'What do the Analytics Hub dashboards show, and which AI models feed them?';
    onAsk?.(q);
  }, [data, onAsk]);

  const insights = data?.insights || [];
  const visibleInsights = showAllInsights ? insights : insights.slice(0, 4);
  const scoreColor = data ? SCORE_COLOR(data.score) : 'gray';
  const narrative = data?.narrative;

  return (
    <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={border} boxShadow="md" mb={6} overflow="hidden" data-testid="analytics-ai-briefing">
      <Box h="3px" bgGradient="linear(to-r, purple.400, blue.400, teal.300)" />
      <Box p={5}>
        {/* header */}
        <Flex justify="space-between" align="flex-start" gap={4} flexWrap="wrap">
          <HStack spacing={4} align="center" minW={0} flex="1">
            {loading && !data ? (
              <Skeleton boxSize="56px" borderRadius="full" />
            ) : (
              <Tooltip label={data ? `AI health score for the range: ${data.score}/100 (${data.scoreLabel})` : ''} hasArrow>
                <CircularProgress value={data?.score ?? 0} color={`${scoreColor}.400`} size="58px" thickness="9px" trackColor={tileBg}>
                  <CircularProgressLabel fontSize="sm" fontWeight="bold">{data?.score ?? '—'}</CircularProgressLabel>
                </CircularProgress>
              </Tooltip>
            )}
            <VStack align="flex-start" spacing={1} minW={0}>
              <HStack spacing={2} flexWrap="wrap">
                <Icon as={MdAutoAwesome} color="purple.400" />
                <Heading size="sm">AI briefing</Heading>
                {data && <Badge colorScheme={scoreColor} variant="subtle">{data.scoreLabel}</Badge>}
                {narrative && (
                  <Tooltip label={narrative.source === 'ollama' ? `Written by ${narrative.model || 'the local LLM'} from the numbers below` : (narrative.fallbackReason || 'Written by the built-in composer from the numbers below')} hasArrow>
                    <Badge variant="outline" colorScheme={narrative.source === 'ollama' ? 'purple' : 'gray'} fontSize="2xs">
                      {narrative.source === 'ollama' ? 'LLM' : 'Built-in'}
                    </Badge>
                  </Tooltip>
                )}
              </HStack>
              {loading && !data
                ? <Skeleton h="14px" w={{ base: '220px', md: '420px' }} />
                : <Text fontSize="sm" color={muted} noOfLines={2}>{data?.headline || ''}</Text>}
            </VStack>
          </HStack>
          <HStack spacing={1}>
            {data && <Text fontSize="xs" color={muted} display={{ base: 'none', md: 'block' }}>Updated {timeAgo(data.generatedAt)}{data.cached ? ' (cached)' : ''}</Text>}
            <Button size="sm" variant="ghost" colorScheme="purple" leftIcon={<MdQuestionAnswer />} onClick={ask}>Ask about this</Button>
            <Tooltip label="Refresh briefing" hasArrow>
              <IconButton size="sm" variant="ghost" aria-label="Refresh briefing" icon={<MdRefresh />} isLoading={refreshing} onClick={() => load({ quiet: true })} />
            </Tooltip>
            <Tooltip label={collapsed ? 'Show briefing' : 'Hide briefing'} hasArrow>
              <IconButton size="sm" variant="ghost" aria-label={collapsed ? 'Show briefing' : 'Hide briefing'} icon={collapsed ? <MdExpandMore /> : <MdExpandLess />} onClick={toggle} />
            </Tooltip>
          </HStack>
        </Flex>

        <Collapse in={!collapsed} animateOpacity>
          {error && !data && (
            <Alert status="warning" borderRadius="md" mt={4}>
              <AlertIcon />
              <AlertDescription fontSize="sm" flex="1">The AI briefing could not be loaded: {error.message}</AlertDescription>
              <Button size="xs" onClick={() => load()}>Retry</Button>
            </Alert>
          )}

          {/* KPI tiles */}
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3} mt={4}>
            {loading && !data
              ? [0, 1, 2, 3].map(i => <Skeleton key={i} h="84px" borderRadius="lg" />)
              : (data?.kpis || []).map(k => <KpiTile key={k.key} k={k} tileBg={tileBg} border={border} muted={muted} sparkColors={spark} />)}
          </SimpleGrid>
          {data?.range && (
            <Text fontSize="2xs" color={muted} mt={2}>
              {data.range.start} → {data.range.end} ({data.range.days} days), compared with {data.range.previous.start} → {data.range.previous.end}.
              {data.unavailable?.length ? ` Not tracked: ${data.unavailable.join(', ')}.` : ''}
            </Text>
          )}

          <SimpleGrid columns={{ base: 1, lg: 5 }} spacing={5} mt={4}>
            {/* findings */}
            <Box gridColumn={{ lg: 'span 3' }} minW={0}>
              <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wide" color={muted} mb={2}>What needs attention</Text>
              {loading && !data ? <SkeletonText noOfLines={5} spacing="3" /> : insights.length === 0 ? (
                <HStack color="green.400" fontSize="sm"><Icon as={MdCheckCircleOutline} /><Text>No issues found in this range.</Text></HStack>
              ) : (
                <VStack align="stretch" spacing={1} data-testid="briefing-insights">
                  {visibleInsights.map(i => {
                    const sev = SEVERITY[i.severity] || SEVERITY.info;
                    return (
                      <Flex key={i.id} gap={3} p={2} borderRadius="md" _hover={{ bg: insightHover }} align="flex-start">
                        <Icon as={sev.icon} color={`${sev.color}.400`} mt="2px" boxSize={4} flexShrink={0} />
                        <Box flex="1" minW={0}>
                          <Text fontSize="sm" fontWeight="semibold">{i.title}</Text>
                          {i.detail && <Text fontSize="xs" color={muted}>{i.detail}</Text>}
                        </Box>
                        {i.action?.path && onNavigate && (
                          <Button size="xs" variant="ghost" rightIcon={<MdChevronRight />} onClick={() => onNavigate(i.action.path)} flexShrink={0}>
                            {i.action.label}
                          </Button>
                        )}
                      </Flex>
                    );
                  })}
                  {insights.length > 4 && (
                    <Button size="xs" variant="link" alignSelf="flex-start" onClick={() => setShowAllInsights(v => !v)} mt={1}>
                      {showAllInsights ? 'Show fewer' : `Show all ${insights.length} findings`}
                    </Button>
                  )}
                </VStack>
              )}
            </Box>

            {/* narrative + models */}
            <Box gridColumn={{ lg: 'span 2' }} bg={accentBg} borderRadius="lg" p={4} minW={0}>
              <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wide" color={muted} mb={2}>Summary</Text>
              {loading && !data ? <SkeletonText noOfLines={5} spacing="3" /> : (
                <Box fontSize="sm"><MiniMarkdown text={narrative?.text || ''} /></Box>
              )}
              {data?.ml && (
                <>
                  <Divider my={3} />
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="xs" color={muted}>AI models in production</Text>
                    <Badge colorScheme={data.ml.mlOnline ? 'green' : 'red'} fontSize="2xs">ML engine {data.ml.mlOnline ? 'online' : 'offline'}</Badge>
                  </HStack>
                  <Wrap spacing={1}>
                    {data.ml.models.map(m => (
                      <WrapItem key={m.key}>
                        <Tooltip label={m.version ? `${m.name} ${m.version}${m.metric !== null && m.metric !== undefined ? ` · ${m.metricLabel} ${m.format === 'ratio' ? `${(m.metric * 100).toFixed(1)}%` : Number(m.metric).toFixed(2)}` : ''}` : `${m.name}: no production version`} hasArrow>
                          <Badge colorScheme={m.version ? (m.activeJob ? 'blue' : 'purple') : 'gray'} variant={m.version ? 'subtle' : 'outline'} cursor={onNavigate ? 'pointer' : 'default'}
                            onClick={onNavigate ? () => onNavigate(`/admin/ai/grading?tab=registry&model=${m.key}`) : undefined}>
                            {m.name}{m.activeJob ? ' · training' : ''}
                          </Badge>
                        </Tooltip>
                      </WrapItem>
                    ))}
                  </Wrap>
                </>
              )}
            </Box>
          </SimpleGrid>
        </Collapse>
      </Box>
    </Box>
  );
}
